import React, { FC, useEffect, useState } from 'react';
import { IoSearch } from 'react-icons/io5';

import { Box, InputProps, Menu, Overlay, Transition } from '@mantine/core';
import { useFocusWithin } from '@mantine/hooks';
import { useWorkspaceConfig } from 'src/frontend/hooks/use-workspace-config';

import { ComponentQuery } from 'models';

import { SearchBarAutocomplete } from 'components/component-overview/components/SearchBarAutocomplete';
import { useComponentSearchQuery } from 'components/component-overview/hooks/use-component-search-query';
import { SearchBox } from 'components/search-box/SearchBox';
import { useApplySearchQuery } from 'hooks/use-apply-search-query';
import { useIsMobile } from 'hooks/use-is-mobile';
import { SearchHistoryScope } from 'hooks/use-search-history';

const SearchBar: FC<{
    size?: InputProps['size'];
    searchSuggestions?: boolean;
    showAutocomplete?: boolean;
    placeholder?: string;
    showSubmit?: boolean;
    onFocus?: () => void;
    onChange?: (query: string) => void;
    componentInstanceDefaults?: Partial<ComponentQuery>;
    historyScope?: SearchHistoryScope;
}> = ({
    size = 'lg',
    showSubmit,
    showAutocomplete = true,
    onFocus,
    onChange: externalOnChange,
    componentInstanceDefaults,
    historyScope,
}) => {
    const { ref, focused } = useFocusWithin();
    const { workspace: inWorkspace, name: workspaceName } = useWorkspaceConfig();

    const isMobile = useIsMobile();

    const { query, setQuery, resetAll } = useComponentSearchQuery();
    const applySearchQuery = useApplySearchQuery({ defaults: componentInstanceDefaults, historyScope });

    const autocompleteOpen = showAutocomplete && focused;

    const [textInput, setTextInput] = useState('');

    useEffect(() => {
        if (focused) return;
        if (textInput === query) return;

        setTextInput(query);
    }, [query, focused]);

    const onChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const next = e.currentTarget.value;

        handleChangeQuery(next);
        externalOnChange?.(next);
    };

    const handleChangeQuery = (query: string) => {
        setTextInput(query);
        setQuery(query);
    };

    return (
        <>
            <Transition mounted={autocompleteOpen} transition="fade" duration={200} timingFunction="ease">
                {(styles) => <Overlay fixed color="#000" backgroundOpacity={0.5} hiddenFrom="sm" style={styles} />}
            </Transition>
            <Menu
                opened={autocompleteOpen}
                position="bottom-start"
                offset={0}
                width="target"
                trapFocus={false}
                returnFocus={false}
                shadow="xl"
                transitionProps={{
                    enterDelay: isMobile ? 300 : 0,
                }}
            >
                <Menu.Target>
                    <Box ref={ref} style={{ zIndex: autocompleteOpen ? 999 : 'auto' }}>
                        <SearchBox
                            value={textInput ?? ''}
                            placeholder={
                                inWorkspace && workspaceName
                                    ? `Search ${workspaceName} products`
                                    : 'Search for products'
                            }
                            leftSection={<IoSearch size={16} />}
                            size={size}
                            autoComplete="off"
                            onChange={onChange}
                            submit={() => {
                                (document.activeElement as HTMLElement)?.blur();
                                applySearchQuery(textInput);
                            }}
                            handleReset={
                                resetAll
                                    ? () => {
                                          resetAll();
                                          setTextInput('');
                                      }
                                    : undefined
                            }
                            autocompleteOpen={autocompleteOpen}
                            showSubmit={showSubmit}
                            onFocus={onFocus}
                        />
                    </Box>
                </Menu.Target>
                {showAutocomplete && (
                    <SearchBarAutocomplete
                        query={textInput}
                        setQuery={(query) => {
                            handleChangeQuery(query);

                            (document.activeElement as HTMLElement)?.blur();
                        }}
                        handleSearch={(_query) => {
                            handleChangeQuery(_query);
                            applySearchQuery(_query);

                            (document.activeElement as HTMLElement)?.blur();
                        }}
                    />
                )}
            </Menu>
        </>
    );
};

export { SearchBar };
